/**
 * Per-stake / per-location breakdown — one tappable row per segment, each
 * showing the same headline stats run over just that segment. Tap a row to
 * filter the whole tracker to it.
 *
 * Per-stake bb/hr here is the honest one: within a single stake there's no
 * cross-stake bb inflation, so the rate reflects how you actually run there.
 */

import type { SegmentStats } from "@/shared/models";
import { ChevronRight } from "@/shared/components/icons";
import {
  bbPerHour,
  hoursLabel,
  money,
  netToneClass,
} from "@/features/tracker/lib/trackerFormat";

export function BreakdownTable({
  segments,
  loading,
  onSelect,
}: {
  segments: SegmentStats[];
  loading?: boolean;
  onSelect: (key: string) => void;
}) {
  if (loading) {
    return <p className="text-sm text-text-muted px-1 py-4">Crunching the numbers…</p>;
  }
  if (segments.length === 0) {
    return (
      <p className="text-sm text-text-muted px-1 py-4">
        No completed sessions to break down yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="px-1 text-xs text-text-muted">Tap a row to filter the tracker to it.</p>
      {segments.map((seg) => {
        const s = seg.stats;
        const rate =
          s.bbPerHour !== null ? bbPerHour(s.bbPerHour) : `${money(s.netPerHour)}/hr`;
        return (
          <button
            key={seg.key}
            type="button"
            onClick={() => onSelect(seg.key)}
            className="group w-full flex items-center justify-between gap-2 rounded-xl border border-border bg-surface px-4 py-3 text-left transition hover:border-accent/40 hover:bg-text/5"
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium text-text truncate">{seg.key}</div>
              <div className="mt-0.5 text-xs text-text-muted">
                {hoursLabel(s.totalHours)} · {s.sessionCount} session
                {s.sessionCount === 1 ? "" : "s"} · {Math.round(s.winRatePct)}% W
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className={`text-base font-semibold tabular-nums ${netToneClass(s.netTotal)}`}>
                {money(s.netTotal)}
              </div>
              <div className={`text-xs tabular-nums ${netToneClass(s.bbPerHour ?? s.netPerHour)}`}>
                {rate}
              </div>
            </div>
            <ChevronRight
              size={16}
              strokeWidth={2}
              className="shrink-0 text-text-muted/50 group-hover:text-accent transition"
            />
          </button>
        );
      })}
    </div>
  );
}
